import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Wand2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  "A cinematic indigo nebula with floating geometric crystals",
  "A futuristic creator workspace, cyan and purple neon, glassmorphism",
  "An aurora over a city of light, ultra-detailed",
];

export default function StudioZone() {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [gallery, setGallery] = useState([]);

  const load = async () => {
    try {
      const r = await api.get("/ai/generations");
      setGallery(r.data);
    } catch (e) {
      console.warn("[StudioZone] gallery load failed", e);
    }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!prompt.trim()) return;
    setBusy(true);
    try {
      await api.post("/ai/image", { prompt });
      setPrompt("");
      await load();
      toast.success("Manifested ✦");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Generation failed";
      toast.error(detail);
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-[#7B2CBF] mb-1">ai studio</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Idea to artifact, in seconds</h1>
        <p className="text-[#64748B] text-sm mt-1">Powered by Gemini Nano Banana. No-code creation surface.</p>
      </div>

      <div className="glass-heavy rounded-3xl p-6 grain">
        <div className="flex items-center gap-2 mb-3">
          <Wand2 size={18} className="text-[#7B2CBF]" />
          <div className="font-display font-bold">Manifest</div>
        </div>
        <textarea
          data-testid="studio-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          placeholder="Describe what should exist…"
          className="nexus-input resize-none"
        />
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {PRESETS.map((p) => (
            <button
              key={p}
              onClick={() => setPrompt(p)}
              data-testid="preset-prompt"
              className="text-xs glass px-3 py-1.5 rounded-full text-[#64748B] hover:text-[#0F172A] transition-all"
            >
              {p.slice(0, 36)}…
            </button>
          ))}
          <button
            data-testid="studio-generate"
            onClick={generate}
            disabled={busy || !prompt.trim()}
            className="ml-auto nexus-btn-primary disabled:opacity-50 flex items-center gap-2"
          >
            <Sparkles size={14} /> {busy ? "Manifesting…" : "Generate"}
          </button>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-[#F5C542] mb-3">your generations</div>
        {gallery.length === 0 ? (
          <div className="glass rounded-2xl p-10 text-center text-[#64748B]">
            Nothing manifested yet. Start with a prompt above.
          </div>
        ) : (
          <div data-testid="studio-gallery" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {gallery.map((g, i) => (
              <motion.div
                key={g.id}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.04 }}
                className="glass rounded-2xl overflow-hidden"
              >
                <img src={g.image_b64} alt={g.prompt} className="w-full h-56 object-cover" />
                <div className="p-3 text-xs text-[#64748B] line-clamp-2">{g.prompt}</div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
